// misc
import { getModelOffers, getModelPrivacy } from '@v4/services/api/v3/offer';
import { updateWindowHistoryReplaceState, getQueryParams, windowHistoryReplaceStateParams } from '@v4/common/utils/url';
import { breakpoint } from '@v4/common/utils/media-query';
import { isEmpty } from 'lodash';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';

// components
import DkOfferCard from '@v4/common/components/molecules/DkOfferCard.vue';

// mixins
import { dkModalAware } from '@v4/common/components/mixins/modal';
import {
    modalWizardCarConfigurator,
    modalServiceOfferBenefits,
    modalWizardLeadForm,
} from '@v4/common/components/mixins/modal-service';
import clickoutPopupEventsHandler from '@v4/common/components/mixins/clickout-popup-events-handler';

// styles
import '@v4/multi-section/mixins/single-offer/_index.scss';
import { EventBus } from '@v4/common/utils/event-bus';

export default ({ fetchedData }) => {
    const components = {
        DkOfferCard,
        ClickoutBox: () => ({
            component: import(
                /* webpackChunkName: "ClickoutBox-vue" */ '@v4/common/components/molecules/ClickoutBox.vue'
            ),
        }),
    };

    if (fetchedData.useSimilarCars && Array.isArray(fetchedData.similarCars) && fetchedData.similarCars.length) {
        components.DkScore = () => ({
            component: import(/* webpackChunkName: "DkScore-vue" */ '@v4/common/components/molecules/DkScore.vue'),
        });

        components.DkBadge = () => ({
            component: import(/* webpackChunkName: "DkBadge-vue" */ '@v4/common/components/atoms/DkBadge.vue'),
        });
    }

    return {
        // Adding the "name" property makes it easier for
        // the component to be found in Vue Dev Tools panel
        name: 'VehicleOfferPage',
        components,
        mixins: [
            dkModalAware,
            modalWizardCarConfigurator,
            modalServiceOfferBenefits,
            gaEventHandler,
            clickoutPopupEventsHandler,
            modalWizardLeadForm,
        ],
        data() {
            return {
                multiSelectionDisabled: true,
                hasError: false,
                isBusy: true,
                requestCount: 0,
                maxRequestCount: 10,
                selectedQueryStringParamName: 'selected_offer',
                similarCarsSwiperOptions: {
                    slidesPerView: 3,
                    spaceBetween: 25,
                    breakpoints: {
                        767: {
                            slidesPerView: 1,
                        },
                        1200: {
                            slidesPerView: 2,
                        },
                    },
                },
                tempItemId: null,
            };
        },
        computed: {
            filter() {
                return this.fetchedData.filter || {};
            },
            items() {
                return this.fetchedData.items || [];
            },
            hasItems() {
                return !!this.items.length;
            },
            selectedItem() {
                return this.items.reduce(
                    (accumulator, item) => (item.items && item.items.find((subitem) => subitem.selected)) || accumulator,
                    null
                );
            },
            itemIsPreSelected() {
                return this.selectedItem && !getQueryParams()[this.selectedQueryStringParamName];
            },
            shouldPoolData() {
                return this.fetchedData.completed === false;
            },
            totalResultText() {
                const offersCount = this.items.length;

                return this.$tc('ds-offer-card.offers-found', offersCount, {
                    n: offersCount,
                });
            },
            sidebarMetaHeadline() {
                const titleText = this.itemIsPreSelected
                    ? this.$t('multi-section-single-offer.suggested-offer')
                    : this.$t('multi-section-single-offer.quote-request');

                const imgTag =
                    this.selectedItem && this.selectedItem.offerLogo && this.selectedItem.offerLogo.url
                        ? `<img
                            class="u-hidden-till--large flex-shrink-0 multi-section-single-offer__partner-logo"
                            src="${this.selectedItem.offerLogo.url}"
                            alt="${this.selectedItem.offerLogo.text}"
                        />`
                        : '';

                return `
                    <div class="d--flex items-center justify-between">
                        <div class="ts--body-small ts-weight--900 color--grey-900">${titleText}</div>
                        ${imgTag}
                    </div>
                `;
            },
            leadFormMetaData() {
                const title =
                    this.fetchedData && this.fetchedData.makeName && this.fetchedData.submodelCommercialName
                        ? `${this.fetchedData.makeName} ${this.fetchedData.submodelCommercialName}`
                        : '';

                const isSubtitleHidden =
                    this.selectedItem &&
                    this.selectedItem.type &&
                    this.selectedItem.type.slug &&
                    this.selectedItem.type.slug === 'LISTING';
                const subtitle = !isSubtitleHidden ? this.selectedItem.title : '';

                const badgeText =
                    this.selectedItem && this.selectedItem.type && this.selectedItem.type.text
                        ? this.selectedItem.type.text
                        : '';

                return { title, subtitle, badgeText };
            },
            showClickoutBox() {
                return this.fetchedData.clickout_section && !isEmpty(this.fetchedData.clickout_section);
            },
            firstSingleOfferParams() {
                const fetchedDataItems = this.fetchedData.items;
                if (!(fetchedDataItems && fetchedDataItems.length > 0)) {
                    return {};
                }

                const firstItem = fetchedDataItems[0];
                if (!(firstItem && firstItem.items.length > 0)) {
                    return {};
                }

                return firstItem.items[0].formData || {};
            },
            hasWizardForm() {
                return !!getQueryParams().feature_toggle_form_wizard;
            },
        },
        beforeMount() {
            this.$on('lead-form-mounted', this.handleLeadFormHiddenFields);
        },
        beforeDestroy() {
            this.$off('lead-form-mounted', this.handleLeadFormHiddenFields);
            EventBus.$off();
        },
        created() {
            this.fetchData();
        },
        mounted() {
            EventBus.$on('LeadForm:input', this.handleDiscoverPriceClick);

            const makeModel = `${this.fetchedData.makeName} ${this.fetchedData.submodelCommercialName}`;
            this.bindPopupClickoutTracking('Vehicle Offers Page', makeModel);
        },
        methods: {
            async handleLeadFormHiddenFields(selectedItem = {}, leadFormWrapperRef) {
                const item = this.selectedItem || selectedItem || {};
                const leadFormWrapper = leadFormWrapperRef || this.$refs.leadFormWrapper || {};

                leadFormWrapper.setLeadFormInputField(
                    'vat',
                    item.audience && item.audience.toLowerCase() === 'business' ? '' : null
                );
                leadFormWrapper.setLeadFormHiddenField('dkwp[promotionId]', item.id);
                leadFormWrapper.setLeadFormHiddenField('dkwp[projectID]', item.project);
                leadFormWrapper.setLeadFormHiddenField('dkwp[poId]', item.poId);

                try {
                    if (!(item && item.project)) {
                        throw new Error('Project ID is missing');
                    }

                    const data = await getModelPrivacy(item.project);
                    const { text, privacies } = data.data;

                    leadFormWrapper.setLeadFormPrivacyFlags(privacies);
                    this.setLeadFormPrivacyText(text);
                } catch (error) {
                    leadFormWrapper.setLeadFormPrivacyFlags(null);
                    this.setLeadFormPrivacyText(null);
                }
            },
            async onOfferSelected({ item }) {
                const promotionId = item.id;
                this.tempItemId = item.id;

                updateWindowHistoryReplaceState({ [this.selectedQueryStringParamName]: promotionId });
                this.pushPromotionClickToDataLayer({
                    promotionId,
                    position: item.index,
                    subtitle: item.subtitle,
                });

                const showForm = this.multiSelectionDisabled ? true : !breakpoint('1024');

                if (showForm) {
                    this.handleModal(item.id, item.index);
                }

                this.pushGtagEvent('select_promotion', item.id, this.fetchedData.events);
            },
            showWizardCarConfigurator(submodelId) {
                const { makeUrlCode, submodelCommercialUrlCode, formId, formData } = this.fetchedData;

                this.clearClickToCallTimer();
                this.openWizardCarConfiguratorModal({
                    make: makeUrlCode,
                    model: submodelCommercialUrlCode,
                    leadFormWrapper: {
                        formId,
                        // eslint-disable-next-line camelcase
                        formData: { ...formData, promotion_id: this.selectedItem.id },
                        singleOfferParams: this.selectedItem.formData,
                    },
                    leadFormMeta: this.leadFormMetaData,
                    leadFormIncentive: this.fetchedData.toast,
                    handleLeadFormHiddenFields: this.handleLeadFormHiddenFields,
                    onDiscoverPrice: () => this.pushGtagEvent('add_to_cart', submodelId, this.fetchedData.events),
                });
            },
            setLeadFormPrivacyText(text) {
                this.$refs.leadFormWrapper.updateLeadFormCtasPrivacyPolicyText(text);
            },
            onRemoveFiltersClick() {
                windowHistoryReplaceStateParams({
                    ...(this.selectedItem && { [this.selectedQueryStringParamName]: this.selectedItem.id }),
                });
                this.fetchData(true);
            },
            fetchData(reset = false) {
                if (reset) {
                    this.fetchedData.completed = false;
                    this.requestCount = 0;
                    this.fetchedData.items.forEach((item) => (item.items = []));
                }

                if (
                    !this.shouldPoolData ||
                    !this.fetchedData.makeUrlCode ||
                    !this.fetchedData.submodelCommercialUrlCode ||
                    this.requestCount > this.maxRequestCount
                ) {
                    this.isBusy = false;

                    if (this.fetchedData && this.fetchedData.events && this.fetchedData.events['view_item_list']) {
                        this.pushGtagEvent('view_item_list', null, this.fetchedData.events);
                    }

                    return;
                }

                this.isBusy = true;
                this.requestCount += 1;
                const delay = 500 * this.requestCount;

                getModelOffers(this.fetchedData.makeUrlCode, this.fetchedData.submodelCommercialUrlCode)
                    .then(({ data }) => {
                        if (data && data.data && data.data.items && data.data.items.length) {
                            this.fetchedData = { ...this.fetchedData, ...data.data };

                            this.$nextTick(() => {
                                // continuing fetching data until `completed` property has value `true`
                                if (this.shouldPoolData) {
                                    return setTimeout(() => this.fetchData(), delay);
                                } else if (this.selectedItem) {
                                    // all data was loaded, set the hidden fields to LeadForm component
                                    this.handleLeadFormHiddenFields();
                                }
                            });
                        } else {
                            // we suppose to not have items when empty or items property doesn't exists
                            this.fetchedData = { ...this.fetchedData, items: [] };
                        }

                        this.isBusy = false;
                    })
                    .catch(({ error, response }) => {
                        if (response) {
                            // the request was made and the server responded with a status code
                            // the falls out of the range of `2xx`
                            this.hasError = true;
                        } else if (error && error.event && error.event.type === 'timeout') {
                            // the request was aborted because of the timeout
                            return setTimeout(() => this.fetchData(), delay);
                        }

                        this.isBusy = false;
                    })
                    .finally(() => {
                        if (this.fetchedData && this.fetchedData.events && this.fetchedData.events['view_item_list']) {
                            this.pushGtagEvent('view_item_list', null, this.fetchedData.events);
                        }
                    });
            },
            handleDiscoverPriceClick() {
                this.pushGtagEvent('begin_checkout', this.tempItemId, this.fetchedData.events);
            },
            async openBenefitsModal(item) {
                return await this.openOfferBenefitsModal(item, this.onSelect);
            },
            onSelect(item) {
                if (item.selected) {
                    /**
                     * Fired when an item was re-selected
                     *
                     * @event re-select
                     * @type {object}
                     */
                    return this.onOfferSelected({ item });
                }

                // handle selection
                if (this.items.length) {
                    // deselect all
                    this.items.forEach((item) => item.items.forEach((subItem) => this.$set(subItem, 'selected', false)));
                    // select the new one
                    this.$set(this.item.items[item.index], 'selected', true);
                }

                const itemIndex = this.items.findIndex((i) => i.id === item.id);

                // track cta buttons click
                this.pushGaEvent(
                    'vehicle offer page',
                    'CTA click',
                    !item.index
                        ? `VOP - ${itemIndex + 1} box CTA button click`
                        : `VOP - ${itemIndex + 1} box ${item.index} sub-item CTA click`
                );

                if (this.isContractHire(item)) {
                    this.pushGaEvent('vehicle offer page', 'ContractHire_VOP_select', 'ContractHire_VOP_select');
                }

                /**
                 * Fired when an item was selected
                 *
                 * @event select
                 * @type {object}
                 */
                this.onOfferSelected({ item });
            },
            handleModal(itemId, itemIndex) {
                if (this.hasWizardForm) {
                    this.openWizardFormModal(itemId, itemIndex);
                } else {
                    this.showWizardCarConfigurator(itemId);
                }
            },
            openWizardFormModal(itemId, index) {
                const {
                    formId,
                    submodelFullName: fullName,
                    makeUrlCode: make,
                    submodelCommercialUrlCode: model,
                } = this.fetchedData;

                const item = this.items[index].items[0];

                if (!item) {
                    return;
                }

                const { formData, finalPrice: price, versionName, type } = item;

                const props = {
                    formId,
                    // eslint-disable-next-line camelcase
                    formData: { ...formData, promotion_id: this.selectedItem.id },
                    fullName,
                    make,
                    model,
                    price,
                    versionName,
                    offerType: type.slug,
                    singleOfferParams: this.selectedItem.formData,
                    promotionId: itemId,
                };

                this.openWizardLeadFormModal(props);
            },
        },
    };
};
